import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
    ArrowRight, ChefHat, BellRing, Martini, ShoppingBasket, Car, Flag, Sailboat, Sparkles,
    BedDouble, Bath, Users, Square, Check, MapPin, KeyRound, Hammer, BookOpen, Award,
    Tag, CalendarCheck, LifeBuoy, Headset, BadgeCheck,
} from "lucide-react";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import {
    VILLAS, COMING_SOON_RESIDENCES, RENTAL_EXPERIENCE, RENTAL_WHY, COLLECTION_CRITERIA,
} from "@/lib/content";
import { useLanguage } from "@/lib/LanguageContext";
import { gaRentalsCollectionView, gaRentalPropertyClick } from "@/lib/analytics";
import useSEO from "@/lib/useSEO";

const DIRECT_BENEFITS = [
    { key: "rate", icon: Tag },
    { key: "planning", icon: CalendarCheck },
    { key: "support", icon: LifeBuoy },
    { key: "concierge", icon: Headset },
    { key: "nofees", icon: BadgeCheck },
];

// HERO_IMAGE — authentic Hacienda 511 infinity pool at sunset over the Sea of Cortez.
const HERO_IMAGE = "/villas/hacienda-511/h511-pool-sunset-01.webp";

// EXPERIENCE_IMAGE — authentic Hacienda 511 covered-terrace dining by the sea
const EXPERIENCE_IMAGE = "/villas/hacienda-511/h511-outdoor-dining-01.webp";

// WHY_IMAGE — authentic Hacienda 511 infinity pool + ocean (portrait crop)
const WHY_IMAGE = "/villas/hacienda-511/h511-pool-ocean-02.webp";

// STANDARDS_IMAGE — authentic Hacienda 511 architecture at night (subtle backdrop)
const STANDARDS_IMAGE = "/villas/hacienda-511/h511-entrance-night-01.webp";

const EXP_ICONS = {
    ChefHat, BellRing, Martini, ShoppingBasket, Car, Flag, Sailboat, Sparkles,
};

const CRITERIA_ICONS = {
    location: MapPin,
    design: BookOpen,
    privacy: KeyRound,
    maintenance: Hammer,
    owner: Award,
    guest: Sparkles,
    concierge: BellRing,
};

const WHY_ICONS = {
    curated: Sparkles,
    concierge: BellRing,
    standards: Award,
    local: MapPin,
};

export default function PacificRentals() {
    const { t, lang } = useLanguage();

    const featured = VILLAS[0]; // Hacienda 511

    useEffect(() => { gaRentalsCollectionView(); }, []);

    useSEO({
        title: lang === "es"
            ? "Rentas de Villas de Lujo Los Cabos | Pacific Rentals"
            : "Luxury Villa Rentals Los Cabos | Pacific Rentals",
        description: lang === "es"
            ? "Una colección curada de residencias privadas excepcionales en Los Cabos, gestionadas profesionalmente por Pacific Key Management."
            : "A curated collection of exceptional private residences in Los Cabos, professionally managed and locally supported by Pacific Key Management.",
        path: "/pacific-rentals",
        keywords: "Pacific Rentals Los Cabos, luxury villa Los Cabos, luxury villa Villas del Mar, Hacienda 511, Villas del Mar villa rental, Pacific Key Management",
        breadcrumbs: [
            { name: "Home", path: "/" },
            { name: "Pacific Rentals", path: "/pacific-rentals" },
        ],
    });

    const featuredName = lang === "es" ? featured.nameEs : featured.nameEn;
    const featuredAmenities = lang === "es" ? featured.amenitiesEs : featured.amenitiesEn;
    const featuredDescription = lang === "es" ? featured.descriptionEs : featured.descriptionEn;

    return (
        <div data-testid="rentals-page">
            {/* HERO */}
            <PageHero
                eyebrow={t("rentals.heroEyebrow")}
                title={<>{t("rentals.heroTitle1")} <span className="italic font-light">{t("rentals.heroTitle2")}</span></>}
                subtitle={t("rentals.heroSubtitle")}
                image={HERO_IMAGE}
            />
            <div className="bg-navy text-pearl py-8 sm:py-10 border-b border-pearl/10">
                <div className="container-luxe flex flex-col sm:flex-row gap-4 sm:gap-6 items-stretch sm:items-center justify-center">
                    <Link to={`/pacific-rentals/${featured.slug}`} onClick={() => gaRentalPropertyClick({ villa_slug: featured.slug, cta_location: "collection_hero" })} className="btn-luxe-light justify-center" data-testid="hero-cta-featured">
                        {t("rentals.heroCtaFeatured")} <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                    </Link>
                    <Link to="/contact" className="btn-luxe-gold justify-center" data-testid="hero-cta-plan">
                        {t("rentals.heroCtaPlan")}
                    </Link>
                </div>
            </div>

            {/* SECTION 2 — Featured Residence */}
            <section className="bg-pearl py-24 sm:py-32" data-testid="featured-section">
                <div className="container-luxe">
                    <Reveal className="max-w-3xl mb-16">
                        <p className="overline text-gold">{t("rentals.featuredEyebrow")}</p>
                        <h2 className="mt-5 font-serif text-4xl sm:text-5xl leading-tight">{t("rentals.featuredTitle")}</h2>
                        <span className="gold-divider mt-6" />
                        <p className="mt-7 text-navy/75 leading-relaxed text-lg italic font-light">{t("rentals.featuredSubtitle")}</p>
                    </Reveal>

                    <Reveal className="grid lg:grid-cols-12 gap-0 lg:gap-0 border border-navy/10" data-testid="featured-card">
                        {/* Image — left */}
                        <div className="lg:col-span-7 relative aspect-[5/4] lg:aspect-auto overflow-hidden group">
                            <img
                                src={featured.image}
                                alt={featuredName}
                                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1800ms] ease-out group-hover:scale-105"
                                loading="lazy"
                            />
                            <div className="absolute top-5 left-5 bg-navy/90 backdrop-blur-sm px-4 py-2 text-[0.65rem] tracking-[0.28em] uppercase text-gold flex items-center gap-2">
                                <Sparkles className="w-3.5 h-3.5 text-gold" strokeWidth={1.25} />
                                {t("rentals.featuredFlagship")}
                            </div>
                        </div>

                        {/* Detail panel — right */}
                        <div className="lg:col-span-5 bg-pearl p-10 lg:p-14 flex flex-col justify-between gap-10">
                            <div>
                                <p className="overline text-gold">{featured.community}</p>
                                <h3 className="mt-3 font-serif text-4xl lg:text-5xl leading-[1.05]">{featuredName}</h3>
                                <p className="mt-3 text-sm text-navy/55 tracking-wide">{featured.location}</p>
                                <span className="gold-divider mt-7" />
                                <p className="mt-7 text-sm text-navy/75 leading-relaxed">{featuredDescription}</p>

                                {/* Amenities — refined two-column list */}
                                <ul className="mt-9 grid sm:grid-cols-2 gap-x-6 gap-y-3" data-testid="featured-amenities">
                                    {featuredAmenities.map((a, i) => (
                                        <li key={i} className="flex items-start gap-3 text-sm text-navy/85">
                                            <Check className="w-3.5 h-3.5 text-gold mt-1 flex-shrink-0" strokeWidth={1.75} />
                                            <span>{a}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            {/* Price + CTA */}
                            <div className="border-t border-navy/10 pt-7">
                                <p className="overline text-navy/55">{lang === "es" ? "Desde" : "Starting From"}</p>
                                <p className="font-serif text-3xl text-navy mt-2" data-testid="featured-price">
                                    ${featured.priceFromUsd.toLocaleString()} <span className="text-base text-navy/55 tracking-wide">USD / {lang === "es" ? "Noche" : "Night"}</span>
                                </p>
                                <Link
                                    to={`/pacific-rentals/${featured.slug}`}
                                    onClick={() => gaRentalPropertyClick({ villa_slug: featured.slug, cta_location: "featured_card" })}
                                    className="btn-luxe-primary mt-7 w-full justify-center"
                                    data-testid="featured-view-cta"
                                >
                                    {t("rentals.featuredViewCta")} <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                                </Link>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* SECTION 3 — The Pacific Rentals Experience */}
            <section className="bg-sand/40 py-24 sm:py-32" data-testid="experience-section">
                <div className="container-luxe">
                    <div className="grid lg:grid-cols-12 gap-14 lg:gap-20 items-center mb-16">
                        <Reveal className="lg:col-span-6 space-y-6">
                            <p className="overline text-gold">{t("rentals.experienceEyebrow")}</p>
                            <h2 className="font-serif text-4xl sm:text-5xl leading-tight">{t("rentals.experienceTitle")}</h2>
                            <span className="gold-divider" />
                            <p className="text-navy/75 leading-relaxed">{t("rentals.experienceBody")}</p>
                        </Reveal>
                        <Reveal delay={120} className="lg:col-span-6">
                            <div className="aspect-[5/4] overflow-hidden">
                                <img src={EXPERIENCE_IMAGE} alt="" className="w-full h-full object-cover" loading="lazy" />
                            </div>
                        </Reveal>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-navy/10">
                        {RENTAL_EXPERIENCE.map((exp, i) => {
                            const Icon = EXP_ICONS[exp.icon] || Sparkles;
                            return (
                                <Reveal
                                    key={exp.key}
                                    delay={i * 60}
                                    className="bg-pearl p-8 min-h-[170px] flex flex-col justify-between"
                                    data-testid={`experience-card-${exp.key}`}
                                >
                                    <Icon className="w-6 h-6 text-gold" strokeWidth={1.25} />
                                    <h3 className="font-serif text-lg leading-snug mt-6">{t(`rentals.exp_${exp.key}`)}</h3>
                                </Reveal>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* SECTION 4 — Why Guests Choose */}
            <section className="bg-pearl py-24 sm:py-32" data-testid="why-section">
                <div className="container-luxe">
                    <Reveal className="max-w-3xl mb-16">
                        <p className="overline text-gold">{t("rentals.whyEyebrow")}</p>
                        <h2 className="mt-5 font-serif text-4xl sm:text-5xl leading-tight">{t("rentals.whyTitle")}</h2>
                    </Reveal>
                    <div className="grid lg:grid-cols-12 gap-14 lg:gap-20 items-start">
                        <Reveal className="lg:col-span-5 lg:sticky lg:top-32">
                            <div className="aspect-[4/5] overflow-hidden">
                                <img src={WHY_IMAGE} alt="" className="w-full h-full object-cover" loading="lazy" />
                            </div>
                        </Reveal>
                        <div className="lg:col-span-7 grid sm:grid-cols-2 gap-x-10 gap-y-12">
                            {RENTAL_WHY.map((k, i) => {
                                const Icon = WHY_ICONS[k] || Sparkles;
                                return (
                                    <Reveal key={k} delay={i * 80} data-testid={`why-card-${k}`}>
                                        <Icon className="w-7 h-7 text-gold mb-5" strokeWidth={1.25} />
                                        <h3 className="font-serif text-2xl leading-tight">{t(`rentals.why_${k}.title`)}</h3>
                                        <span className="block w-8 h-px bg-gold my-4" />
                                        <p className="text-sm text-navy/70 leading-relaxed">{t(`rentals.why_${k}.body`)}</p>
                                    </Reveal>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </section>

            {/* SECTION 5 — Our Collection Standards */}
            <section
                className="relative bg-navy text-pearl py-24 sm:py-32 overflow-hidden"
                data-testid="standards-section"
            >
                <div className="absolute inset-0">
                    <img src={STANDARDS_IMAGE} alt="" className="w-full h-full object-cover opacity-20" loading="lazy" />
                    <div className="absolute inset-0 bg-navy/80" />
                </div>
                <div className="relative container-luxe grid lg:grid-cols-12 gap-14 lg:gap-20">
                    <Reveal className="lg:col-span-5 space-y-6">
                        <p className="overline text-gold">{t("rentals.standardsEyebrow")}</p>
                        <h2 className="font-serif text-pearl text-4xl sm:text-5xl leading-tight">{t("rentals.standardsTitle")}</h2>
                        <span className="gold-divider" />
                        <p className="text-pearl/75 leading-relaxed">{t("rentals.standardsBody")}</p>
                    </Reveal>
                    <div className="lg:col-span-7 space-y-8">
                        <div className="grid sm:grid-cols-2 gap-x-10 gap-y-5">
                            {COLLECTION_CRITERIA.map((c, i) => {
                                const Icon = CRITERIA_ICONS[c] || MapPin;
                                return (
                                    <Reveal
                                        key={c}
                                        delay={i * 60}
                                        className="flex items-center gap-4 border-t border-pearl/15 pt-5"
                                        data-testid={`criterion-${c}`}
                                    >
                                        <Icon className="w-5 h-5 text-gold flex-shrink-0" strokeWidth={1.25} />
                                        <span className="font-serif text-pearl text-lg">{t(`rentals.c_${c}`)}</span>
                                    </Reveal>
                                );
                            })}
                        </div>
                        <Reveal delay={500} className="border-t border-pearl/15 pt-8">
                            <p className="text-pearl/75 leading-relaxed text-sm italic">
                                {t("rentals.standardsClosing")}
                            </p>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* SECTION 6 — Coming Soon */}
            <section className="bg-pearl py-24 sm:py-32" data-testid="coming-soon-section">
                <div className="container-luxe">
                    <Reveal className="max-w-3xl mb-14">
                        <p className="overline text-gold">{t("rentals.comingEyebrow")}</p>
                        <h2 className="mt-5 font-serif text-4xl sm:text-5xl leading-tight">{t("rentals.comingTitle")}</h2>
                        <span className="gold-divider mt-6" />
                        <p className="mt-7 text-navy/70 leading-relaxed max-w-2xl">{t("rentals.comingSubtitle")}</p>
                    </Reveal>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10">
                        {COMING_SOON_RESIDENCES.map((r, i) => (
                            <Reveal key={r.key} delay={i * 90} data-testid={`coming-card-${r.key}`}>
                                <div className="relative aspect-[4/5] overflow-hidden bg-navy border border-pearl/10">
                                    <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
                                        <span className="overline text-gold mb-5 tracking-[0.32em]">{t("rentals.comingLabel")}</span>
                                        <span className="block w-12 h-px bg-gold mb-6" />
                                        <h3 className="font-serif text-pearl text-3xl leading-tight">{lang === "es" ? r.nameEs : r.nameEn}</h3>
                                    </div>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* SECTION — Why Book Direct */}
            <section className="bg-pearl py-24 sm:py-32 border-t border-navy/5" data-testid="why-direct-section">
                <div className="container-luxe">
                    <Reveal className="max-w-3xl mb-16">
                        <p className="overline text-gold">{t("rentals.directEyebrow")}</p>
                        <h2 className="mt-5 font-serif text-4xl sm:text-5xl leading-tight">{t("rentals.directTitle")}</h2>
                        <span className="gold-divider mt-6" />
                        <p className="mt-7 text-navy/70 leading-relaxed max-w-2xl">{t("rentals.directSubtitle")}</p>
                    </Reveal>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-px bg-navy/10">
                        {DIRECT_BENEFITS.map((b, i) => {
                            const Icon = b.icon;
                            return (
                                <Reveal key={b.key} delay={i * 60} className="bg-pearl p-8 min-h-[180px] flex flex-col justify-between" data-testid={`direct-benefit-${b.key}`}>
                                    <Icon className="w-6 h-6 text-gold" strokeWidth={1.25} />
                                    <h3 className="font-serif text-lg leading-snug mt-6">{t(`rentals.direct_${b.key}`)}</h3>
                                </Reveal>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* SECTION 7 — For Homeowners */}
            <section className="bg-sand/40 py-24 sm:py-32" data-testid="owner-section">
                <div className="container-luxe grid lg:grid-cols-12 gap-16 items-start">
                    <Reveal className="lg:col-span-5 space-y-6 lg:sticky lg:top-32">
                        <p className="overline text-gold">{t("rentals.ownerEyebrow")}</p>
                        <h2 className="font-serif text-4xl sm:text-5xl leading-tight">{t("rentals.ownerTitle")}</h2>
                        <span className="gold-divider" />
                        <p className="text-navy/75 leading-relaxed">{t("rentals.ownerBody")}</p>
                        <Link to="/contact" className="btn-luxe-primary" data-testid="rentals-owner-cta">
                            {t("rentals.ownerCta")} <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                        </Link>
                    </Reveal>

                    <div className="lg:col-span-7 grid sm:grid-cols-2 gap-px bg-navy/10">
                        {["ob1", "ob2", "ob3", "ob4"].map((k, i) => (
                            <Reveal key={k} delay={i * 90} className="bg-pearl p-10 min-h-[220px]" data-testid={`owner-benefit-${i}`}>
                                <span className="text-gold font-serif text-2xl">0{i + 1}</span>
                                <h3 className="mt-5 font-serif text-2xl">{t(`rentals.${k}.title`)}</h3>
                                <span className="block w-8 h-px bg-gold my-4" />
                                <p className="text-sm text-navy/70 leading-relaxed">{t(`rentals.${k}.body`)}</p>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* FINAL CTA */}
            <section className="bg-navy text-pearl py-24 sm:py-32 text-center" data-testid="final-cta-section">
                <div className="container-luxe max-w-2xl mx-auto space-y-7">
                    <p className="overline text-gold">{t("rentals.outroEyebrow")}</p>
                    <h2 className="font-serif text-pearl text-4xl sm:text-5xl leading-tight">{t("rentals.outroTitle")}</h2>
                    <p className="text-pearl/75 leading-relaxed">{t("rentals.outroBody")}</p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center pt-2">
                        <Link to="/contact" className="btn-luxe-light justify-center" data-testid="cta-request-availability">
                            {t("rentals.outroCtaAvailability")} <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                        </Link>
                        <Link to="/concierge" className="btn-luxe-gold justify-center" data-testid="cta-contact-concierge">
                            {t("rentals.outroCtaConcierge")}
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
